import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "section"]
  static values = {
    storageKey: { type: String, default: "sidebar-section-order" }
  }

  connect() {
    this.restoreOrder()
    this.setupDragHandlers()
    this.updateFirstSectionBorder()
    this.createPlaceholder()
  }

  createPlaceholder() {
    this.placeholder = document.createElement("div")
    this.placeholder.className = "h-12 bg-indigo-100 dark:bg-indigo-900/30 border-2 border-dashed border-indigo-400 dark:border-indigo-500 rounded-lg mx-1 my-2"
  }

  setupDragHandlers() {
    // Container-level handlers
    this.containerTarget.addEventListener("dragover", this.handleContainerDragOver.bind(this))
    this.containerTarget.addEventListener("drop", this.handleContainerDrop.bind(this))

    this.sectionTargets.forEach(section => {
      section.setAttribute("draggable", "true")
      section.addEventListener("dragstart", this.handleDragStart.bind(this))
      section.addEventListener("dragend", this.handleDragEnd.bind(this))
    })
  }

  handleDragStart(event) {
    this.draggedElement = event.currentTarget
    this.draggedElement.classList.add("opacity-50")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", event.currentTarget.dataset.sectionId)
  }

  handleDragEnd(event) {
    event.currentTarget.classList.remove("opacity-50")
    this.hidePlaceholder()
    this.draggedElement = null
  }

  hidePlaceholder() {
    if (this.placeholder.parentNode) {
      this.placeholder.parentNode.removeChild(this.placeholder)
    }
  }

  handleContainerDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    if (!this.draggedElement) return

    const container = this.containerTarget
    const sections = [...this.sectionTargets].filter(s => s !== this.draggedElement)

    // Find the section we're hovering over
    let targetSection = null
    let insertBefore = true

    for (const section of sections) {
      const rect = section.getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2

      if (event.clientY < midpoint) {
        targetSection = section
        insertBefore = true
        break
      } else if (event.clientY >= rect.top && event.clientY <= rect.bottom) {
        targetSection = section
        insertBefore = false
      }
    }

    // Position placeholder
    if (targetSection) {
      if (insertBefore) {
        container.insertBefore(this.placeholder, targetSection)
      } else {
        container.insertBefore(this.placeholder, targetSection.nextSibling)
      }
    } else {
      // Append at end if below all sections
      container.appendChild(this.placeholder)
    }
  }

  handleContainerDrop(event) {
    event.preventDefault()

    if (!this.draggedElement) return

    const container = this.containerTarget

    // Insert the dragged element where the placeholder is
    if (this.placeholder.parentNode === container) {
      container.insertBefore(this.draggedElement, this.placeholder)
    }

    this.hidePlaceholder()
    this.saveOrder()
    this.updateFirstSectionBorder()
  }

  updateFirstSectionBorder() {
    // Hide border-t on first section, show on others
    this.sectionTargets.forEach((section, index) => {
      if (index === 0) {
        section.classList.remove("border-t", "pt-2")
        section.classList.add("border-t-0")
      } else {
        section.classList.remove("border-t-0")
        section.classList.add("border-t", "border-gray-200", "dark:border-gray-700", "pt-2")
      }
    })
  }

  saveOrder() {
    const order = this.sectionTargets.map(section => section.dataset.sectionId)
    localStorage.setItem(this.storageKeyValue, JSON.stringify(order))
  }

  restoreOrder() {
    const savedOrder = localStorage.getItem(this.storageKeyValue)
    if (!savedOrder) return

    try {
      const order = JSON.parse(savedOrder)
      const container = this.containerTarget
      const sections = new Map()

      // Build a map of section ID to element
      this.sectionTargets.forEach(section => {
        sections.set(section.dataset.sectionId, section)
      })

      // Reorder sections based on saved order
      order.forEach(sectionId => {
        const section = sections.get(sectionId)
        if (section) {
          container.appendChild(section)
        }
      })

      // Append any sections not in the saved order (new sections)
      this.sectionTargets.forEach(section => {
        if (!order.includes(section.dataset.sectionId)) {
          container.appendChild(section)
        }
      })
    } catch (e) {
      console.error("Failed to restore sidebar order:", e)
    }
  }

  resetOrder() {
    localStorage.removeItem(this.storageKeyValue)
    window.location.reload()
  }
}
